import React from 'react';
import { Upload, Button, Badge, Progress, Space, Typography, Card, Tooltip } from 'antd';
import { 
    PlusOutlined, 
    EyeOutlined, 
    DeleteOutlined, 
    CheckCircleOutlined,
    SyncOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import './AttachmentUpload.css';

const { Text } = Typography;

const AttachmentUploadCard = ({ 
    type,
    isRequired,
    fileList,
    uploadLoading,
    uploadProgress,
    uploadStatus,
    onFileSelect,
    onRemove,
    validationError
}) => {
    const hasFile = fileList && fileList.length > 0;
    const currentFile = hasFile ? fileList[0] : null;
    const isUploading = uploadLoading;
    const progress = uploadProgress || 0;
    const status = uploadStatus || 'default'; // default, uploading, success, error

    const getStatusBadge = () => {
        if (status === 'success') {
            return <Badge status="success" text="Đã tải lên" />;
        } else if (status === 'uploading') {
            return <Badge status="processing" text="Đang tải..." />;
        } else if (status === 'error') {
            return <Badge status="error" text="Lỗi" />;
        } else {
            return <Badge status="default" text="Chưa chọn" />;
        }
    };

    const uploadProps = {
        listType: "picture-card",
        maxCount: 1,
        accept: 'image/jpeg,image/png,image/gif,image/heic,image/heif,application/pdf',
        fileList: fileList || [],
        beforeUpload: (file) => {
            // Validate
            const isImage = file.type.startsWith('image/');
            const isPDF = file.type === 'application/pdf';

            if (!isImage && !isPDF) {
                return Upload.LIST_IGNORE;
            }

            const isLt20M = file.size / 1024 / 1024 <= 20;
            if (!isLt20M) {
                return Upload.LIST_IGNORE;
            }

            // Just add to fileList, don't upload yet
            onFileSelect(file);
            return false;
        },
        onRemove: () => {
            onRemove();
            return true;
        },
        showUploadList: false // We'll render custom list
    };

    return (
        <Card 
            size="small"
            style={{ 
                height: '100%'
            }}
        >
            {/* Header */}
            <div style={{ marginBottom: 8 }}>
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <Space>
                        <Text strong>
                            {type.name}
                            {isRequired && <span style={{ color: '#ff4d4f' }}> *</span>}
                        </Text>
                    </Space>
                    {getStatusBadge()}
                </Space>
            </div>

            {/* Upload Area */}
            <div style={{ marginBottom: 12 }}>
                {!hasFile ? (
                    <Upload {...uploadProps}>
                        <div style={{ 
                            width: '100%',
                            height: 120,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px dashed #d9d9d9',
                            borderRadius: 8,
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                        }}
                        className="upload-placeholder"
                        >
                            <PlusOutlined style={{ fontSize: 24, color: '#999' }} />
                            <Text type="secondary" style={{ marginTop: 8 }}>
                                Chọn file
                            </Text>
                        </div>
                    </Upload>
                ) : (
                    <div>
                        {/* File Preview */}
                        <div style={{
                            width: '100%',
                            height: 120,
                            border: '1px solid #d9d9d9',
                            borderRadius: 8,
                            overflow: 'hidden',
                            position: 'relative',
                            background: '#fafafa'
                        }}>
                            {currentFile.thumbUrl || currentFile.url ? (
                                <img 
                                    src={currentFile.thumbUrl || currentFile.url} 
                                    alt={currentFile.name}
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover' 
                                    }}
                                />
                            ) : (
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Text type="secondary">PDF</Text>
                                </div>
                            )}

                            {/* Upload Progress Overlay */}
                            {isUploading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(0,0,0,0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                }}>
                                    <Progress 
                                        type="circle" 
                                        percent={progress} 
                                        width={60}
                                        strokeColor={{
                                            '0%': '#108ee9',
                                            '100%': '#87d068',
                                        }}
                                    />
                                    <Text style={{ color: 'white', marginTop: 8 }}>
                                        Đang tải...
                                    </Text>
                                </div>
                            )}

                            {/* Success Overlay */}
                            {status === 'success' && (
                                <div style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                }}>
                                    <CheckCircleOutlined 
                                        style={{ 
                                            fontSize: 24, 
                                            color: '#52c41a',
                                            background: 'white',
                                            borderRadius: '50%'
                                        }} 
                                    />
                                </div>
                            )}
                        </div>

                        {/* File Info */}
                        <div style={{ marginTop: 8 }}>
                            <Text 
                                ellipsis 
                                style={{ fontSize: 12, display: 'block' }}
                                title={currentFile.name}
                            >
                                {currentFile.name}
                            </Text>
                        </div>

                        {/* Actions */}
                        <Space style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}>
                            {currentFile.uploaded && status === 'success' && (
                                <Tooltip title="Xem file đã tải">
                                    <Button 
                                        size="small" 
                                        icon={<EyeOutlined />}
                                        onClick={() => {
                                            if (currentFile.url || currentFile.thumbUrl) {
                                                window.open(currentFile.url || currentFile.thumbUrl, '_blank');
                                            }
                                        }}
                                    >
                                        Xem file
                                    </Button>
                                </Tooltip>
                            )}
                            
                            {status === 'error' && (
                                <Tooltip title="Chọn lại file">
                                    <Upload {...uploadProps}>
                                        <Button 
                                            size="small" 
                                            type="primary"
                                            danger
                                            icon={<SyncOutlined />}
                                        >
                                            Thử lại
                                        </Button>
                                    </Upload>
                                </Tooltip>
                            )}
                            
                            <Tooltip title="Xóa">
                                <Button 
                                    size="small" 
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={onRemove}
                                    disabled={isUploading}
                                />
                            </Tooltip>
                        </Space>
                    </div>
                )}
            </div>

            {/* File Requirements */}
            <div style={{ fontSize: 11, color: '#999' }}>
                <div>Cho phép: {type.allowed_types}</div>
                <div>Tối đa: {type.max_size / 1024} MB</div>
            </div>

            {/* Validation Error */}
            {validationError && (
                <div style={{ 
                    color: '#ff4d4f', 
                    fontSize: 14, 
                    marginTop: 8,
                    lineHeight: '1.5715'
                }}>
                    {validationError}
                </div>
            )}

            <style jsx>{`
                .upload-placeholder:hover {
                    border-color: #1890ff !important;
                }
            `}</style>
        </Card>
    );
};

AttachmentUploadCard.propTypes = {
    type: PropTypes.object.isRequired,
    isRequired: PropTypes.bool.isRequired,
    fileList: PropTypes.array,
    uploadLoading: PropTypes.bool,
    uploadProgress: PropTypes.number,
    uploadStatus: PropTypes.string,
    onFileSelect: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    validationError: PropTypes.string
};

export default AttachmentUploadCard;
